import { INTERPOLATORS } from 'app/modules/editor/model/interpolators';
import { Path } from 'app/modules/editor/model/paths';
import {
  ColorProperty,
  Inspectable,
  InterpolatorProperty,
  NumberProperty,
  PathProperty,
  Property,
} from 'app/modules/editor/model/properties';
import { isFinite, uniqueId } from 'lodash-es';
type AnimationBlockType = 'path' | 'color' | 'number';

/**
 * An animation block is an individual layer property tween (property animation).
 */
export abstract class AnimationBlock {
  static from(obj: ConstructorArgs) {
    switch (obj.type) {
      case 'path':
        return new PathAnimationBlock(obj);
      case 'color':
        return new ColorAnimationBlock(obj);
      case 'number':
        return new NumberAnimationBlock(obj);
      default:
        throw new Error('invalid block type: ' + obj.type);
    }
  }

  protected constructor(obj: ConstructorArgs) {
    this.id = obj.id || uniqueId();
    this.layerId = obj.layerId;
    this.propertyName = obj.propertyName;
    this.startTime = obj.startTime || 0;
    this.endTime = obj.endTime || 100;
    if (this.startTime > this.endTime) {
      // TODO: don't let this happen (usually results in behavior that seems weird to users)
      const tmp = this.endTime;
      this.endTime = this.startTime;
      this.startTime = tmp;
    }
    // TODO: use the correct default interpolator for import svg/avd/property input
    this.interpolator = obj.interpolator || INTERPOLATORS[0].value;
    this.fromValue = obj.fromValue;
    this.toValue = obj.toValue;
    this.type = obj.type;
  }

  toJSON() {
    return {
      id: this.id,
      layerId: this.layerId,
      propertyName: this.propertyName,
      startTime: this.startTime,
      endTime: this.endTime,
      interpolator: this.interpolator,
      type: this.type,
      fromValue: this.fromValue,
      toValue: this.toValue,
    };
  }

  clone() {
    return AnimationBlock.from(this);
  }

  abstract isAnimatable(): boolean;
}
Property.register(
  new NumberProperty('startTime', { min: 0, isInteger: true }),
  new NumberProperty('endTime', { min: 0, isInteger: true }),
  new InterpolatorProperty('interpolator'),
)(AnimationBlock);

/**
 * An animation block that animates the 'pathData' property.
 */
export class PathAnimationBlock extends AnimationBlock {
  // @Override
  toJSON() {
    return Object.assign(super.toJSON(), {
      fromValue: this.fromValue ? this.fromValue.getPathString() : '',
      toValue: this.toValue ? this.toValue.getPathString() : '',
    });
  }

  // @Override
  isAnimatable() {
    return !!this.fromValue && !!this.toValue && this.fromValue.isMorphableWith(this.toValue);
  }
}
Property.register(new PathProperty('fromValue'), new PathProperty('toValue'))(PathAnimationBlock);

/**
 * An animation block that animates a color property.
 */
export class ColorAnimationBlock extends AnimationBlock {
  // @Override
  isAnimatable() {
    // TODO should this be more specific (i.e. check if valid color values?)
    return !!this.fromValue && !!this.toValue;
  }
}
Property.register(
  new ColorProperty('fromValue'),
  new ColorProperty('toValue'),
)(ColorAnimationBlock);

/**
 * An animation block that animates a number property.
 */
export class NumberAnimationBlock extends AnimationBlock {
  // @Override
  isAnimatable() {
    return isFinite(this.fromValue) && isFinite(this.toValue);
  }
}
Property.register(
  new NumberProperty('fromValue'),
  new NumberProperty('toValue'),
)(NumberAnimationBlock);

interface AnimationBlockArgs {
  id?: string;
  layerId: string;
  propertyName: string;
  startTime?: number;
  endTime?: number;
  // A preset's 'value' key (see INTERPOLATORS), or a curve as pathInterpolator path data.
  interpolator?: string;
  fromValue: any;
  toValue: any;
  type: AnimationBlockType;
}

export interface AnimationBlock extends Required<AnimationBlockArgs>, Inspectable {}
export interface ConstructorArgs extends AnimationBlockArgs {}

export interface PathAnimationBlock {
  // Undefined when the block was added to a layer that hasn't been given a path yet.
  fromValue: Path | undefined;
  toValue: Path | undefined;
  clone(): PathAnimationBlock;
}

export interface ColorAnimationBlock {
  fromValue: string;
  toValue: string;
  clone(): ColorAnimationBlock;
}

export interface NumberAnimationBlock {
  fromValue: number;
  toValue: number;
  clone(): NumberAnimationBlock;
}
